/*
 * RcReconnectBanner — shown on the Sales Dialer and Reports pages only.
 *
 * The app auto-renews each rep's RingCentral access pass in the background and
 * on-use, so this banner stays hidden while things are healthy. It appears ONLY
 * when an automatic renewal ultimately failed because the rep's longer-lived
 * refresh token is itself dead/revoked (backend flags `needs_reconnect` while
 * KEEPING the saved connection). It auto-clears the moment a renewal succeeds
 * again or the rep reconnects manually.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { API, useAuth } from "@/App";
import { AlertTriangle } from "lucide-react";

export default function RcReconnectBanner() {
  const { token, user } = useAuth();
  const navigate = useNavigate();
  const [show, setShow] = useState(false);

  useEffect(() => {
    if (!token || !user) { setShow(false); return; }
    let cancelled = false;
    const check = async () => {
      try {
        const r = await axios.get(`${API}/integrations/ringcentral/oauth/status`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const d = r.data || {};
        if (!cancelled) setShow(Boolean(d.connected && d.needs_reconnect));
      } catch {
        if (!cancelled) setShow(false);
      }
    };
    check();
    const id = setInterval(check, 60_000);
    // Instant clear when the rep completes the OAuth popup in Settings.
    const onMsg = (ev) => {
      if (ev?.data && ev.data.type === "rc-oauth" && ev.data.success) check();
    };
    window.addEventListener("message", onMsg);
    return () => { cancelled = true; clearInterval(id); window.removeEventListener("message", onMsg); };
  }, [token, user]);

  if (!show) return null;

  return (
    <div
      data-testid="rc-reconnect-banner"
      style={{
        display: "flex", alignItems: "center", gap: 10,
        padding: "10px 16px", background: "#fef2f2",
        borderBottom: "1px solid #fecaca", color: "#991b1b", fontSize: 13,
      }}
    >
      <AlertTriangle style={{ width: 16, height: 16, flexShrink: 0 }} />
      <span style={{ fontWeight: 600 }}>RingCentral connection expired</span>
      <span style={{ color: "#b91c1c" }}>
        — automatic reconnect couldn’t renew your session. Calls and recordings may fail until you reconnect.
      </span>
      <button
        data-testid="rc-reconnect-btn"
        onClick={() => navigate("/settings")}
        style={{
          marginLeft: "auto", background: "#b91c1c", color: "#fff", border: 0,
          borderRadius: 6, padding: "6px 14px", fontSize: 12, fontWeight: 600, cursor: "pointer",
        }}
      >
        Reconnect
      </button>
    </div>
  );
}
